import type { routes } from '@semios/app-platform-banyan-route-definitions'
import {
  getValueTypeFromEmitterType,
  IRRIGATION_ACTIVITY_APPLIED_VALUE_TYPE_PREFIX,
} from '@semios/app-platform-values-helpers-and-types'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import type { StackedChartSection } from 'components/StackedChart/types'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { getAllPointsForFlowMeter } from 'utils/setSelectedFieldAsset/_utils/getArgsForAnchorCoordinates'
import { chooseAmongstUnAggHourlyAndDaily } from '../../chooseAmongstUnAggHourlyAndDaily'
import { getIrrigationZoneArgs } from './_utils/getIrrigationZoneArgs'
import { getPressureSensorArgs } from './_utils/getPressureSensorArgs'
import { flowMeterChart } from './flowMeter/flowMeterChart'
import { getIrrigationFlowMeterArgs } from './flowMeter/getIrrigationFlowMeterArgs'
import { pressureSensorsChart } from './pressureSensors/pressureSensorsChart'
import { irrigationActivityZonesIntervalsChart } from './zones/irrigationActivityZonesIntervalsChart'

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedValueGroups.irrigation_activity_pressure) return {}

  if (!selectedFieldAssets.irrigationZoneEmitter) {
    const irrigationFlowMeterArgs = getIrrigationFlowMeterArgs({
      selectedValueGroups,
      selectedFieldAssets,
    })

    return irrigationFlowMeterArgs
  }

  const intervalToRequest = chooseAmongstUnAggHourlyAndDaily()

  const { irrigationZoneId, emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(
    selectedFieldAssets.irrigationZoneEmitter,
  )

  const valueTypeForIntervals = getValueTypeFromEmitterType(
    IRRIGATION_ACTIVITY_APPLIED_VALUE_TYPE_PREFIX,
    emitterType,
  )

  const irrigationZoneArgs = getIrrigationZoneArgs({
    selectedValueGroups,
    selectedFieldAssets,
    irrigationZoneIds: [Number(irrigationZoneId)],
  })

  const pressureSensorArgs = getPressureSensorArgs({
    selectedValueGroups,
    selectedFieldAssets,
    emitterType,
    valueTypeForIntervals,
    intervalToRequest,
  })

  const irrigationFlowMeterArgs = getIrrigationFlowMeterArgs({
    selectedValueGroups,
    selectedFieldAssets,
  })

  const mergedPressureSensorArgsAndFlowMeterArgs = {
    points: {
      lngLats: [
        ...(pressureSensorArgs.points?.lngLats || []),
        ...(irrigationFlowMeterArgs.points?.lngLats || []),
      ],
      valuesRequested: {
        ...pressureSensorArgs.points?.valuesRequested,
        ...irrigationFlowMeterArgs.points?.valuesRequested,
      },
    },
  }

  return {
    ...mergedPressureSensorArgsAndFlowMeterArgs,
    ...irrigationZoneArgs,
  }
}

export const content = ({
  data,
  selectedFieldAssets,
  updateData,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  updateData: (pathToSet: string, dataToSet: unknown) => void
}): StackedChartSection[] => {
  const sectionsToShow: (StackedChartSection | null)[] = [
    pressureSensorsChart({ data, selectedFieldAssets }),
    irrigationActivityZonesIntervalsChart({ data, selectedFieldAssets }),
  ]

  const isUserFreeRegional = isUserOnlyAFreeRegionalUser()

  const flowMeterHasPermission = checkAuthorization({
    permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS',
    entity: isUserFreeRegional ? 'FOR_ANY_ENTITY' : 'FOR_ANY_PROPERTY',
  })

  // we'll only show the chart section if there's at least one property with a flow meter
  if (flowMeterHasPermission) {
    const { properties } = fieldAssetStore.getState()
    const property = properties?.[Number(selectedFieldAssets.property)]
    const flowMeterPointsForProperty = getAllPointsForFlowMeter(Object.values(property?.points ?? {}))

    sectionsToShow.push(
      flowMeterChart({
        data,
        points: flowMeterPointsForProperty,
        selectedFieldAssets,
        updateData,
      }),
    )
  }

  return sectionsToShow.filter((chart): chart is StackedChartSection => Boolean(chart))
}
